import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { useLongPress, type LongPressPoint } from "../lib/useLongPress";

/**
 * Generic, accessible tree (WAI-ARIA `tree` pattern). Used for the location
 * code list in sprint 1 and for the position structure in sprint 2, so it
 * knows nothing about the node shape — every accessor is a prop.
 *
 * Keyboard (focus is a roving tabindex on the row):
 *   ArrowUp/ArrowDown  previous / next visible row
 *   ArrowRight         expand, or move to first child when already expanded
 *   ArrowLeft          collapse, or move to parent when already collapsed
 *   Home / End         first / last visible row
 *   Enter / Space      select the focused row
 *
 * Expansion is controlled when `expandedIds` is given (pair it with
 * `onExpandedChange`), otherwise internal and seeded from `defaultExpandedDepth`.
 *
 * Lazy loading: `getChildren` may return `null` for "not loaded yet". Opening
 * such a node calls `onExpand(node)`; a loading row appears only when the
 * promise takes longer than {@link LOADING_DELAY_MS} (a fast answer would
 * otherwise flash the placeholder and shift the tree), and stays until it
 * settles. The caller is expected to swap the node's children in `nodes` by
 * then. `getHasChildren` decides whether a chevron is shown at all (defaults
 * to "children loaded and non-empty, or not loaded and `onExpand` given").
 */
export type TreeViewProps<T> = {
  nodes: T[];
  getId: (node: T) => string;
  getLabel: (node: T) => ReactNode;
  getChildren: (node: T) => T[] | undefined | null;
  /** Whether the node can be expanded even when its children are not loaded yet. */
  getHasChildren?: (node: T) => boolean;
  /** Called when a node whose children are `null` is opened; resolve after `nodes` carries them. */
  onExpand?: (node: T) => Promise<void>;
  selectedId: string | null;
  onSelect: (node: T) => void;
  renderIcon?: (node: T) => ReactNode;
  renderBadge?: (node: T) => ReactNode;
  /**
   * Row menu: a right click, or a long press with a finger (iPad Safari has no right click — hard
   * rule 12). The default browser menu is suppressed when given. `point` = viewport coordinates.
   */
  onContextMenu?: (node: T, point: LongPressPoint) => void;
  /** Levels expanded initially in uncontrolled mode: 0 = all collapsed, 1 = roots open (default). */
  defaultExpandedDepth?: number;
  /** Controlled expansion state. */
  expandedIds?: ReadonlySet<string>;
  onExpandedChange?: (next: Set<string>) => void;
  ariaLabel?: string;
  /** Rendered instead of the tree when `nodes` is empty. */
  emptyText?: ReactNode;
  /** aria-labels of the chevron button (Serbian defaults; pass t(...) to localise). */
  expandLabel?: string;
  collapseLabel?: string;
  /** Text of the placeholder row while `onExpand` is pending. */
  loadingLabel?: string;
};

type FlatRow<T> = { node: T; id: string; depth: number; parentId: string | null; hasChildren: boolean };

/** How long a lazy load may take before the "loading" row is shown. */
const LOADING_DELAY_MS = 150;

export function TreeView<T>({
  nodes,
  getId,
  getLabel,
  getChildren,
  getHasChildren,
  onExpand,
  selectedId,
  onSelect,
  renderIcon,
  renderBadge,
  onContextMenu,
  defaultExpandedDepth = 1,
  expandedIds,
  onExpandedChange,
  ariaLabel,
  emptyText,
  expandLabel = "Proširi",
  collapseLabel = "Skupi",
  loadingLabel = "Učitavanje…",
}: TreeViewProps<T>) {
  const longPress = useLongPress<T>(onContextMenu);
  /** Nodes whose loading row is visible (slow loads only). */
  const [loadingIds, setLoadingIds] = useState<ReadonlySet<string>>(() => new Set());
  /** Nodes with an `onExpand` in flight — guards against a double fetch. */
  const pendingIds = useRef(new Set<string>());

  const hasKids = useCallback(
    (n: T): boolean => {
      if (getHasChildren) return getHasChildren(n);
      const kids = getChildren(n);
      if (kids === null) return !!onExpand;
      return (kids?.length ?? 0) > 0;
    },
    [getHasChildren, getChildren, onExpand],
  );

  const isControlled = expandedIds !== undefined;
  const [internalExpanded, setInternalExpanded] = useState<Set<string>>(() =>
    collectToDepth(nodes, getId, getChildren, defaultExpandedDepth),
  );
  const expanded: ReadonlySet<string> = isControlled ? expandedIds : internalExpanded;

  const setExpanded = useCallback(
    (next: Set<string>) => {
      if (!isControlled) setInternalExpanded(next);
      onExpandedChange?.(next);
    },
    [isControlled, onExpandedChange],
  );

  // Parent lookup for ArrowLeft + auto-reveal of the selected node.
  const parentOf = useMemo(() => {
    const map = new Map<string, string | null>();
    const walk = (list: T[], parent: string | null) => {
      for (const n of list) {
        const id = getId(n);
        map.set(id, parent);
        walk(getChildren(n) ?? [], id);
      }
    };
    walk(nodes, null);
    return map;
  }, [nodes, getId, getChildren]);

  // When the selection changes (e.g. a node was just created under a collapsed
  // parent) make sure every ancestor is open so the row is actually visible.
  useEffect(() => {
    if (!selectedId) return;
    const missing: string[] = [];
    let cur = parentOf.get(selectedId) ?? null;
    while (cur) {
      if (!expanded.has(cur)) missing.push(cur);
      cur = parentOf.get(cur) ?? null;
    }
    if (missing.length === 0) return;
    const next = new Set(expanded);
    missing.forEach((id) => next.add(id));
    setExpanded(next);
    // `expanded` is intentionally not a dependency: this only reacts to a new selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, parentOf]);

  const rows = useMemo<FlatRow<T>[]>(() => {
    const out: FlatRow<T>[] = [];
    const walk = (list: T[], depth: number, parentId: string | null) => {
      for (const n of list) {
        const id = getId(n);
        const kids = getChildren(n) ?? [];
        out.push({ node: n, id, depth, parentId, hasChildren: hasKids(n) });
        if (kids.length > 0 && expanded.has(id)) walk(kids, depth + 1, id);
      }
    };
    walk(nodes, 0, null);
    return out;
  }, [nodes, expanded, getId, getChildren, hasKids]);
  const rowById = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);

  const [focusedId, setFocusedId] = useState<string | null>(null);
  const rowRefs = useRef(new Map<string, HTMLDivElement>());

  const focusRow = useCallback((id: string) => {
    setFocusedId(id);
    rowRefs.current.get(id)?.focus();
  }, []);

  // The one row that is reachable with Tab: focused > selected > first.
  const tabbableId = useMemo(() => {
    const visible = new Set(rows.map((r) => r.id));
    if (focusedId && visible.has(focusedId)) return focusedId;
    if (selectedId && visible.has(selectedId)) return selectedId;
    return rows[0]?.id ?? null;
  }, [rows, focusedId, selectedId]);

  function toggle(id: string, node: T) {
    const next = new Set(expanded);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
      if (onExpand && getChildren(node) === null && !pendingIds.current.has(id)) {
        pendingIds.current.add(id);
        const timer = setTimeout(() => setLoadingIds((cur) => new Set(cur).add(id)), LOADING_DELAY_MS);
        onExpand(node).finally(() => {
          clearTimeout(timer);
          pendingIds.current.delete(id);
          setLoadingIds((cur) => {
            if (!cur.has(id)) return cur;
            const s = new Set(cur);
            s.delete(id);
            return s;
          });
        });
      }
    }
    setExpanded(next);
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>, row: FlatRow<T>) {
    const idx = rows.findIndex((r) => r.id === row.id);
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (idx < rows.length - 1) focusRow(rows[idx + 1].id);
        break;
      case "ArrowUp":
        e.preventDefault();
        if (idx > 0) focusRow(rows[idx - 1].id);
        break;
      case "ArrowRight":
        e.preventDefault();
        if (!row.hasChildren) break;
        if (!expanded.has(row.id)) toggle(row.id, row.node);
        else if (idx < rows.length - 1) focusRow(rows[idx + 1].id);
        break;
      case "ArrowLeft":
        e.preventDefault();
        if (row.hasChildren && expanded.has(row.id)) toggle(row.id, row.node);
        else if (row.parentId) focusRow(row.parentId);
        break;
      case "Home":
        e.preventDefault();
        if (rows.length) focusRow(rows[0].id);
        break;
      case "End":
        e.preventDefault();
        if (rows.length) focusRow(rows[rows.length - 1].id);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        onSelect(row.node);
        break;
    }
  }

  if (nodes.length === 0) {
    return <div className="empty">{emptyText ?? "—"}</div>;
  }

  // Rendered recursively so the DOM nests `role="group"` under each parent
  // (screen readers announce level/size from the structure), while keyboard
  // navigation works over the flat `rows` list above.
  const renderLevel = (list: T[], depth: number): ReactNode => (
    <ul role={depth === 0 ? "tree" : "group"} className={depth === 0 ? "tree" : "tree-children"} aria-label={depth === 0 ? ariaLabel : undefined}>
      {list.map((n) => {
        const id = getId(n);
        const kidsRaw = getChildren(n);
        const kids = kidsRaw ?? [];
        const hasChildren = hasKids(n);
        const isOpen = hasChildren && expanded.has(id);
        const isLoading = isOpen && loadingIds.has(id);
        const isSelected = id === selectedId;
        const row = rowById.get(id);
        return (
          <li key={id} role="none">
            <div
              ref={(el) => {
                if (el) rowRefs.current.set(id, el);
                else rowRefs.current.delete(id);
              }}
              role="treeitem"
              aria-level={depth + 1}
              aria-selected={isSelected}
              aria-expanded={hasChildren ? isOpen : undefined}
              tabIndex={id === tabbableId ? 0 : -1}
              className={`tree-row${isSelected ? " is-selected" : ""}`}
              data-id={id}
              onClick={() => onSelect(n)}
              onContextMenu={onContextMenu ? (e) => { e.preventDefault(); onContextMenu(n, { x: e.clientX, y: e.clientY }); } : undefined}
              {...(onContextMenu ? longPress(n) : undefined)}
              onFocus={() => setFocusedId(id)}
              onKeyDown={(e) => row && onKeyDown(e, row)}
            >
              {hasChildren ? (
                <button
                  type="button"
                  className="tree-toggle"
                  tabIndex={-1}
                  data-open={isOpen ? "true" : "false"}
                  aria-label={isOpen ? collapseLabel : expandLabel}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(id, n);
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </button>
              ) : (
                <span className="tree-toggle is-leaf" aria-hidden="true" />
              )}
              {renderIcon && <span className="tree-icon">{renderIcon(n)}</span>}
              <span className="tree-label">{getLabel(n)}</span>
              {renderBadge && <span className="tree-badge">{renderBadge(n)}</span>}
            </div>
            {isLoading ? (
              <ul role="group" className="tree-children">
                <li role="none">
                  <div className="tree-row tree-loading" aria-busy="true">{loadingLabel}</div>
                </li>
              </ul>
            ) : (
              isOpen && kids.length > 0 && renderLevel(kids, depth + 1)
            )}
          </li>
        );
      })}
    </ul>
  );

  return <>{renderLevel(nodes, 0)}</>;
}

function collectToDepth<T>(
  nodes: T[],
  getId: (n: T) => string,
  getChildren: (n: T) => T[] | undefined | null,
  depth: number,
): Set<string> {
  const out = new Set<string>();
  const walk = (list: T[], level: number) => {
    if (level >= depth) return;
    for (const n of list) {
      const kids = getChildren(n) ?? [];
      if (kids.length > 0) {
        out.add(getId(n));
        walk(kids, level + 1);
      }
    }
  };
  walk(nodes, 0);
  return out;
}
